import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAccountDataScreens,
  createDataScreen,
  updateDataScreen,
  deleteDataScreen,
  duplicateDataScreen,
  exportScreenConfig,
  subscribeToDataScreens,
  getDefaultThemes,
  getGridPositions
} from '../services/dataScreen';
import { getAccountDashboards } from '../services/dashboard';
import './DataScreen.css';

function DataScreen() {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [screens, setScreens] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScreenModal, setShowScreenModal] = useState(false);
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);

  const defaultThemes = getDefaultThemes();
  const gridPositions = getGridPositions();

  // Screen form state
  const [screenForm, setScreenForm] = useState({
    name: '',
    description: '',
    theme: defaultThemes[0],
    autoRotate: false,
    rotateInterval: 10,
    refreshInterval: 60
  });

  // Layout item form
  const [layoutForm, setLayoutForm] = useState({
    type: 'dashboard',
    dashboardId: '',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    zIndex: 1
  });

  useEffect(() => {
    if (accountId) {
      loadData();

      const unsubscribe = subscribeToDataScreens(accountId, (screensData) => {
        setScreens(screensData);
      });

      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [accountId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [screensData, dashboardsData] = await Promise.all([
        getAccountDataScreens(accountId),
        getAccountDashboards(accountId)
      ]);

      setScreens(screensData);
      setDashboards(dashboardsData);
    } catch (err) {
      console.error('Error loading data:', err);
      alert('載入失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScreen = async (e) => {
    e.preventDefault();

    if (!screenForm.name.trim()) {
      alert('請輸入大屏名稱');
      return;
    }

    try {
      const screen = await createDataScreen({
        ...screenForm,
        accountId: accountId || 'default',
        layout: []
      });
      alert('數據大屏已創建');
      resetScreenForm();
      setShowScreenModal(false);
      setSelectedScreen(screen);
    } catch (err) {
      console.error('Error creating screen:', err);
      alert('創建失敗：' + err.message);
    }
  };

  const handleUpdateScreen = async (e) => {
    e.preventDefault();

    if (!editingScreen) return;

    try {
      await updateDataScreen(editingScreen.id, screenForm);
      alert('數據大屏已更新');
      resetScreenForm();
      setEditingScreen(null);
    } catch (err) {
      console.error('Error updating screen:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleDeleteScreen = async (screenId, screenName) => {
    if (!window.confirm(`確定要刪除大屏「${screenName}」嗎？`)) return;

    try {
      await deleteDataScreen(screenId);
      if (selectedScreen?.id === screenId) {
        setSelectedScreen(null);
      }
      alert('數據大屏已刪除');
    } catch (err) {
      console.error('Error deleting screen:', err);
      alert('刪除失敗：' + err.message);
    }
  };

  const handleAddLayoutItem = async () => {
    if (!selectedScreen) return;

    if (layoutForm.type === 'dashboard' && !layoutForm.dashboardId) {
      alert('請選擇儀表板');
      return;
    }

    try {
      const newLayout = [
        ...(selectedScreen.layout || []),
        {
          ...layoutForm,
          id: Date.now().toString()
        }
      ];

      await updateDataScreen(selectedScreen.id, { layout: newLayout });
      setShowLayoutModal(false);
      resetLayoutForm();

      // Reload screen
      const updatedScreens = await getAccountDataScreens(accountId);
      const updated = updatedScreens.find(s => s.id === selectedScreen.id);
      setSelectedScreen(updated);
    } catch (err) {
      console.error('Error adding layout item:', err);
      alert('添加失敗：' + err.message);
    }
  };

  const handleRemoveLayoutItem = async (itemId) => {
    if (!selectedScreen) return;

    try {
      const newLayout = selectedScreen.layout.filter(item => item.id !== itemId);
      await updateDataScreen(selectedScreen.id, { layout: newLayout });

      // Reload screen
      const updatedScreens = await getAccountDataScreens(accountId);
      const updated = updatedScreens.find(s => s.id === selectedScreen.id);
      setSelectedScreen(updated);
    } catch (err) {
      console.error('Error removing layout item:', err);
      alert('移除失敗：' + err.message);
    }
  };

  const handleDuplicateScreen = async (screenId) => {
    try {
      const newScreen = await duplicateDataScreen(screenId, accountId || 'default');
      alert('數據大屏已複製');
      setSelectedScreen(newScreen);
    } catch (err) {
      console.error('Error duplicating screen:', err);
      alert('複製失敗：' + err.message);
    }
  };

  const handleExportScreen = () => {
    if (!selectedScreen) return;
    exportScreenConfig(selectedScreen);
  };

  const handlePreviewScreen = () => {
    if (!selectedScreen) return;
    window.open(`/data-screen-view/${selectedScreen.id}`, '_blank');
  };

  const resetScreenForm = () => {
    setScreenForm({
      name: '',
      description: '',
      theme: defaultThemes[0],
      autoRotate: false,
      rotateInterval: 10,
      refreshInterval: 60
    });
  };

  const resetLayoutForm = () => {
    setLayoutForm({
      type: 'dashboard',
      dashboardId: '',
      x: 0,
      y: 0,
      width: 100,
      height: 100,
      zIndex: 1
    });
  };

  const getDashboardName = (dashboardId) => {
    const dashboard = dashboards.find(d => d.id === dashboardId);
    return dashboard ? dashboard.name : '未知儀表板';
  };

  const applyGridPosition = (position) => {
    setLayoutForm({
      ...layoutForm,
      x: position.x,
      y: position.y,
      width: position.width,
      height: position.height
    });
  };

  if (loading) {
    return (
      <div className="data-screen">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="data-screen">
      <header className="page-header">
        <div className="header-left">
          <h1>數據大屏</h1>
          <p>自定義數據可視化大屏</p>
        </div>
        <div className="header-right">
          {selectedScreen && (
            <>
              <button
                className="btn btn-success"
                onClick={handlePreviewScreen}
              >
                👁️ 預覽
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleExportScreen}
              >
                📥 匯出
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowLayoutModal(true)}
              >
                ➕ 添加元素
              </button>
            </>
          )}
          <button
            className="btn btn-primary"
            onClick={() => {
              resetScreenForm();
              setShowScreenModal(true);
            }}
          >
            ➕ 新增大屏
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            返回
          </button>
        </div>
      </header>

      <div className="screen-container">
        {/* Screens Sidebar */}
        <div className="screens-sidebar">
          <h2>我的大屏</h2>
          {screens.length === 0 ? (
            <div className="empty-screens">
              <p>尚無數據大屏</p>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  resetScreenForm();
                  setShowScreenModal(true);
                }}
              >
                創建第一個大屏
              </button>
            </div>
          ) : (
            <div className="screens-list">
              {screens.map((screen) => (
                <div
                  key={screen.id}
                  className={`screen-item ${selectedScreen?.id === screen.id ? 'active' : ''}`}
                >
                  <div className="screen-info" onClick={() => setSelectedScreen(screen)}>
                    <h4>{screen.name}</h4>
                    <p>{screen.description}</p>
                    <div className="screen-meta">
                      <span className="layout-count">{screen.layout?.length || 0} 個元素</span>
                    </div>
                  </div>
                  <div className="screen-item-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDuplicateScreen(screen.id)}
                      title="複製"
                    >
                      📋
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteScreen(screen.id, screen.name)}
                      title="刪除"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Screen Preview */}
        <div className="screen-preview">
          {!selectedScreen ? (
            <div className="empty-preview">
              <h3>請選擇數據大屏</h3>
              <p>在左側選擇或創建大屏以開始</p>
            </div>
          ) : (
            <>
              <div className="preview-header">
                <h3>{selectedScreen.name}</h3>
                <div className="theme-indicator">
                  <span
                    className="theme-color"
                    style={{ backgroundColor: selectedScreen.theme.backgroundColor }}
                  />
                  <span
                    className="theme-color"
                    style={{ backgroundColor: selectedScreen.theme.textColor }}
                  />
                  <span
                    className="theme-color"
                    style={{ backgroundColor: selectedScreen.theme.accentColor }}
                  />
                </div>
              </div>

              <div
                className="preview-canvas"
                style={{
                  backgroundColor: selectedScreen.theme.backgroundColor,
                  color: selectedScreen.theme.textColor
                }}
              >
                {selectedScreen.layout && selectedScreen.layout.length === 0 ? (
                  <div className="empty-canvas">
                    <p style={{ color: selectedScreen.theme.textColor }}>
                      點擊「➕ 添加元素」開始編輯大屏
                    </p>
                  </div>
                ) : (
                  selectedScreen.layout?.map((item) => (
                    <div
                      key={item.id}
                      className="layout-item"
                      style={{
                        left: `${item.x}%`,
                        top: `${item.y}%`,
                        width: `${item.width}%`,
                        height: `${item.height}%`,
                        zIndex: item.zIndex,
                        borderColor: selectedScreen.theme.accentColor
                      }}
                    >
                      <div className="layout-item-header">
                        <span className="layout-type">{item.type}</span>
                        {item.type === 'dashboard' && (
                          <span className="layout-title">
                            {getDashboardName(item.dashboardId)}
                          </span>
                        )}
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveLayoutItem(item.id)}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="layout-item-body">
                        {item.type === 'dashboard' && (
                          <div className="dashboard-placeholder">
                            📊 儀表板預覽
                          </div>
                        )}
                        {item.type === 'text' && (
                          <div className="text-content">{item.content || '文字內容'}</div>
                        )}
                        {item.type === 'image' && (
                          <div className="image-placeholder">🖼️ 圖片</div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Screen Modal */}
      {(showScreenModal || editingScreen) && (
        <div className="modal-overlay" onClick={() => {
          setShowScreenModal(false);
          setEditingScreen(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingScreen ? '編輯大屏' : '新增大屏'}</h2>
              <button className="close-btn" onClick={() => {
                setShowScreenModal(false);
                setEditingScreen(null);
              }}>
                ✕
              </button>
            </div>

            <form onSubmit={editingScreen ? handleUpdateScreen : handleCreateScreen} className="screen-form">
              <div className="form-group">
                <label>大屏名稱 *</label>
                <input
                  type="text"
                  value={screenForm.name}
                  onChange={(e) => setScreenForm({ ...screenForm, name: e.target.value })}
                  placeholder="例如: 銷售數據總覽"
                  required
                />
              </div>

              <div className="form-group">
                <label>描述</label>
                <textarea
                  value={screenForm.description}
                  onChange={(e) => setScreenForm({ ...screenForm, description: e.target.value })}
                  placeholder="大屏用途說明"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>主題</label>
                <div className="theme-selector">
                  {defaultThemes.map((theme, idx) => (
                    <div
                      key={idx}
                      className={`theme-option ${screenForm.theme.name === theme.name ? 'active' : ''}`}
                      onClick={() => setScreenForm({ ...screenForm, theme })}
                    >
                      <div className="theme-colors">
                        <span style={{ backgroundColor: theme.backgroundColor }} />
                        <span style={{ backgroundColor: theme.textColor }} />
                        <span style={{ backgroundColor: theme.accentColor }} />
                      </div>
                      <div className="theme-name">{theme.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={screenForm.autoRotate}
                    onChange={(e) => setScreenForm({ ...screenForm, autoRotate: e.target.checked })}
                  />
                  <span>啟用自動輪播</span>
                </label>
              </div>

              {screenForm.autoRotate && (
                <div className="form-group">
                  <label>輪播間隔（秒）</label>
                  <input
                    type="number"
                    value={screenForm.rotateInterval}
                    onChange={(e) => setScreenForm({ ...screenForm, rotateInterval: parseInt(e.target.value) })}
                    min="5"
                    max="300"
                  />
                </div>
              )}

              <div className="form-group">
                <label>資料更新間隔（秒）</label>
                <input
                  type="number"
                  value={screenForm.refreshInterval}
                  onChange={(e) => setScreenForm({ ...screenForm, refreshInterval: parseInt(e.target.value) })}
                  min="10"
                  max="3600"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowScreenModal(false);
                  setEditingScreen(null);
                }}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingScreen ? '更新' : '創建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Layout Modal */}
      {showLayoutModal && (
        <div className="modal-overlay" onClick={() => setShowLayoutModal(false)}>
          <div className="modal-content layout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>添加元素</h2>
              <button className="close-btn" onClick={() => setShowLayoutModal(false)}>
                ✕
              </button>
            </div>

            <div className="layout-form">
              <div className="form-group">
                <label>元素類型</label>
                <select
                  value={layoutForm.type}
                  onChange={(e) => setLayoutForm({ ...layoutForm, type: e.target.value })}
                >
                  <option value="dashboard">儀表板</option>
                  <option value="text">文字</option>
                  <option value="image">圖片</option>
                </select>
              </div>

              {layoutForm.type === 'dashboard' && (
                <div className="form-group">
                  <label>選擇儀表板</label>
                  <select
                    value={layoutForm.dashboardId}
                    onChange={(e) => setLayoutForm({ ...layoutForm, dashboardId: e.target.value })}
                  >
                    <option value="">選擇儀表板...</option>
                    {dashboards.map(dashboard => (
                      <option key={dashboard.id} value={dashboard.id}>
                        {dashboard.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>快速定位</label>
                <div className="grid-positions">
                  {gridPositions.map((pos, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="grid-btn"
                      onClick={() => applyGridPosition(pos)}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="position-inputs">
                <div className="form-group">
                  <label>X (%)</label>
                  <input
                    type="number"
                    value={layoutForm.x}
                    onChange={(e) => setLayoutForm({ ...layoutForm, x: parseInt(e.target.value) })}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="form-group">
                  <label>Y (%)</label>
                  <input
                    type="number"
                    value={layoutForm.y}
                    onChange={(e) => setLayoutForm({ ...layoutForm, y: parseInt(e.target.value) })}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="form-group">
                  <label>寬度 (%)</label>
                  <input
                    type="number"
                    value={layoutForm.width}
                    onChange={(e) => setLayoutForm({ ...layoutForm, width: parseInt(e.target.value) })}
                    min="10"
                    max="100"
                  />
                </div>
                <div className="form-group">
                  <label>高度 (%)</label>
                  <input
                    type="number"
                    value={layoutForm.height}
                    onChange={(e) => setLayoutForm({ ...layoutForm, height: parseInt(e.target.value) })}
                    min="10"
                    max="100"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowLayoutModal(false)}>
                  取消
                </button>
                <button type="button" className="btn btn-primary" onClick={handleAddLayoutItem}>
                  添加
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataScreen;
